// Default trading setups, tags, mistakes, and confluences

// Setup templates with description, rules, and recommended confluences
export const defaultSetupTemplates = [
  { 
    id: 'breakout',
    name: 'Breakout', 
    description: 'Trading breakouts from consolidation patterns, ranges, or key levels.',
    rules: [],
    recommendedConfluences: ['high-volume', 'delta-confirmation', 'market-trending'],
    isDefault: true 
  },
  { 
    id: 'pullback',
    name: 'Pullback', 
    description: 'Entering on a pullback/retracement within a larger trend.',
    rules: [],
    recommendedConfluences: ['ema-bullish-stack', 'ema-bearish-stack', 'at-sd-zone', 'rsi-oversold', 'rsi-overbought'],
    isDefault: true 
  },
  { 
    id: 'reversal',
    name: 'Reversal', 
    description: 'Trading reversals at key support/resistance or after exhaustion moves.',
    rules: [],
    recommendedConfluences: ['rsi-divergence', 'delta-divergence', 'volume-climax', 'at-sd-zone', 'at-pdh', 'at-pdl'],
    isDefault: true 
  },
  { 
    id: 'momentum',
    name: 'Momentum', 
    description: 'Trading strong directional moves with high volume/delta.',
    rules: [],
    recommendedConfluences: ['delta-confirmation', 'high-volume', 'market-trending', 'ema-bullish-stack', 'ema-bearish-stack'],
    isDefault: true 
  },
  { 
    id: 'support-resistance',
    name: 'Support/Resistance', 
    description: 'Trading bounces or reactions at key S/R levels.',
    rules: [],
    recommendedConfluences: ['at-sd-zone', 'at-pdh', 'at-pdl', 'at-psych-level', 'delta-divergence'],
    isDefault: true 
  },
  { 
    id: 'trend-following',
    name: 'Trend Following', 
    description: 'Trading in the direction of the established trend.',
    rules: [],
    recommendedConfluences: ['ema-bullish-stack', 'ema-bearish-stack', 'market-trending', 'delta-confirmation'],
    isDefault: true 
  },
  { 
    id: 'mean-reversion',
    name: 'Mean Reversion', 
    description: 'Trading moves back to the mean/VWAP after extended moves.',
    rules: [],
    recommendedConfluences: ['rsi-oversold', 'rsi-overbought', 'at-vwap', 'volume-climax', 'market-ranging'],
    isDefault: true 
  },
  { 
    id: 'gap-fill',
    name: 'Gap Fill', 
    description: 'Trading the fill of overnight or intraday gaps.',
    rules: [],
    recommendedConfluences: ['at-open', 'rsi-oversold', 'rsi-overbought', 'delta-divergence'],
    isDefault: true 
  },
  { 
    id: 'squeeze',
    name: 'Squeeze', 
    description: 'Trading volatility expansion after periods of low volatility/compression.',
    rules: [],
    recommendedConfluences: ['low-volume', 'market-ranging', 'delta-confirmation', 'high-volume'],
    isDefault: true 
  },
  { 
    id: 'flag-pennant',
    name: 'Flag/Pennant', 
    description: 'Trading continuation patterns like flags and pennants.',
    rules: [],
    recommendedConfluences: ['market-trending', 'ema-bullish-stack', 'ema-bearish-stack', 'delta-confirmation'],
    isDefault: true 
  }
];

// Legacy support - array of just names for backward compatibility
export const defaultSetups = defaultSetupTemplates.map(s => s.name);

export const defaultTags = [
  'FOMC Day', 'Jobless Claims', 'Triple Witching', 'CPI', 'NFP', 
  'GDP', 'Earnings', 'Options Expiry', 'Dividend Ex-Date', 'Holiday Trading'
];

export const defaultMistakes = [
  'FOMO Entry', 'Early Exit', 'Late Entry', 'No Stop Loss', 'Moved Stop Loss',
  'Oversize Position', 'Revenge Trading', 'Ignored Setup Rules', 'Emotional Decision'
];

// Confluences - additional market conditions to track for pattern analysis
// Organized by category for easier selection
export const defaultConfluences = [
  // EMA Alignment (Higher Timeframe)
  { id: 'ema-above-all', name: 'Above All EMAs', category: 'HTF EMA' },
  { id: 'ema-below-all', name: 'Below All EMAs', category: 'HTF EMA' },
  { id: 'ema-bullish-stack', name: 'Bullish EMA Stack', category: 'HTF EMA' },
  { id: 'ema-bearish-stack', name: 'Bearish EMA Stack', category: 'HTF EMA' },
  { id: 'ema-mixed', name: 'Mixed/Choppy EMAs', category: 'HTF EMA' },
  
  // RSI State
  { id: 'rsi-oversold', name: 'RSI Oversold (<30)', category: 'RSI' },
  { id: 'rsi-overbought', name: 'RSI Overbought (>70)', category: 'RSI' },
  { id: 'rsi-divergence', name: 'RSI Divergence', category: 'RSI' },
  { id: 'rsi-neutral', name: 'RSI Neutral (30-70)', category: 'RSI' },
  
  // Delta/Volume
  { id: 'delta-divergence', name: 'Delta Divergence', category: 'Delta/Volume' },
  { id: 'delta-confirmation', name: 'Strong Delta Confirmation', category: 'Delta/Volume' },
  { id: 'high-volume', name: 'High Volume', category: 'Delta/Volume' },
  { id: 'low-volume', name: 'Low Volume', category: 'Delta/Volume' },
  { id: 'volume-climax', name: 'Volume Climax', category: 'Delta/Volume' },
  
  // Key Levels
  { id: 'at-psych-level', name: 'At Psychological Level', category: 'Key Levels' },
  { id: 'at-sd-zone', name: 'At Supply/Demand Zone', category: 'Key Levels' },
  { id: 'at-pdh', name: 'At Previous Day High', category: 'Key Levels' },
  { id: 'at-pdl', name: 'At Previous Day Low', category: 'Key Levels' },
  { id: 'at-hod', name: 'At High of Day', category: 'Key Levels' },
  { id: 'at-lod', name: 'At Low of Day', category: 'Key Levels' },
  { id: 'at-vwap', name: 'At VWAP', category: 'Key Levels' },
  { id: 'at-open', name: 'At Opening Price', category: 'Key Levels' },
  
  // Price Action
  { id: 'pa-engulfing', name: 'Engulfing Candle', category: 'Price Action' },
  { id: 'pa-pinbar', name: 'Pin Bar / Rejection', category: 'Price Action' },
  { id: 'pa-doji', name: 'Doji / Indecision', category: 'Price Action' },
  { id: 'pa-breakout', name: 'Breakout', category: 'Price Action' },
  { id: 'pa-breakdown', name: 'Breakdown', category: 'Price Action' },
  { id: 'pa-false-break', name: 'False Breakout', category: 'Price Action' },
  { id: 'pa-double-top', name: 'Double Top', category: 'Price Action' },
  { id: 'pa-double-bottom', name: 'Double Bottom', category: 'Price Action' },
  { id: 'pa-higher-low', name: 'Higher Low', category: 'Price Action' },
  { id: 'pa-lower-high', name: 'Lower High', category: 'Price Action' },
  { id: 'pa-consolidation', name: 'Consolidation Break', category: 'Price Action' },
  { id: 'pa-gap-fill', name: 'Gap Fill', category: 'Price Action' },
  
  // Market Type
  { id: 'market-trending', name: 'Trending Day', category: 'Market Type' },
  { id: 'market-ranging', name: 'Ranging Day', category: 'Market Type' },
  { id: 'market-choppy', name: 'Choppy/Erratic', category: 'Market Type' },
  { id: 'market-news', name: 'News-Driven', category: 'Market Type' },
  { id: 'market-low-vol', name: 'Low Volatility Day', category: 'Market Type' },
  { id: 'market-high-vol', name: 'High Volatility Day', category: 'Market Type' },
];

// Helper to get confluences grouped by category (sorted alphabetically)
export const getConfluencesByCategory = (confluences) => {
  const grouped = confluences.reduce((acc, conf) => {
    const category = conf.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(conf);
    return acc;
  }, {});
  // Sort items within each category alphabetically by name
  Object.values(grouped).forEach(items => items.sort((a, b) => a.name.localeCompare(b.name)));
  // Return with categories sorted alphabetically
  const sorted = {};
  Object.keys(grouped).sort((a, b) => a.localeCompare(b)).forEach(key => { sorted[key] = grouped[key]; });
  return sorted;
};

// Session color palette — used in JournalView banner and Calendar pills
export const SESSION_COLORS = {
  blue:   { border: 'border-l-blue-500',   bg: 'bg-blue-500/20',   text: 'text-blue-400',   dot: 'bg-blue-500',   hex: '#3b82f6' },
  purple: { border: 'border-l-purple-500', bg: 'bg-purple-500/20', text: 'text-purple-400', dot: 'bg-purple-500', hex: '#a855f7' },
  green:  { border: 'border-l-green-500',  bg: 'bg-green-500/20',  text: 'text-green-400',  dot: 'bg-green-500',  hex: '#22c55e' },
  amber:  { border: 'border-l-amber-500',  bg: 'bg-amber-500/20',  text: 'text-amber-400',  dot: 'bg-amber-500',  hex: '#f59e0b' },
  rose:   { border: 'border-l-rose-500',   bg: 'bg-rose-500/20',   text: 'text-rose-400',   dot: 'bg-rose-500',   hex: '#f43f5e' },
  cyan:   { border: 'border-l-cyan-500',   bg: 'bg-cyan-500/20',   text: 'text-cyan-400',   dot: 'bg-cyan-500',   hex: '#06b6d4' },
};

export const getSessionColorDef = (colorKey) => SESSION_COLORS[colorKey] || SESSION_COLORS.blue;
